import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PeriodFilter } from '../components/PeriodFilter';
import { ArrowUpRight, ArrowDownLeft, Scale } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VATTrackingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchVATData();
  }, [periodFilter]);

  const fetchVATData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (periodFilter.startDate) params.append('start_date', periodFilter.startDate);
      if (periodFilter.endDate) params.append('end_date', periodFilter.endDate);
      const response = await axios.get(`${API_URL}/vat-tracking?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load VAT data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading VAT data...</div></div>;
  }

  const tabs = [
    { key: 'summary', label: 'Summary' },
    { key: 'outputs', label: 'VAT Outputs (Sales)' },
    { key: 'inputs', label: 'VAT Inputs (Purchases/Expenses)' },
  ];

  return (
    <div className="space-y-6" data-testid="vat-tracking-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold text-foreground">VAT Tracking</h1>
        <p className="text-muted-foreground">Category B - 18% VAT (inclusive in price)</p>
      </div>

      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-border shadow-sm p-6" data-testid="output-vat-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <ArrowUpRight className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Output VAT (Sales)</p>
              <p className="text-2xl font-bold text-red-600">RWF {formatNumber(data.output_vat.total_vat)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Excl. VAT: RWF {formatNumber(data.output_vat.total_excl_vat)}</p>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-6" data-testid="input-vat-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <ArrowDownLeft className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Input VAT (Purchases/Expenses)</p>
              <p className="text-2xl font-bold text-green-600">RWF {formatNumber(data.input_vat.total_vat)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Excl. VAT: RWF {formatNumber(data.input_vat.total_excl_vat)}</p>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-6" data-testid="net-vat-card">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${data.net_vat >= 0 ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Scale className={`w-5 h-5 ${data.net_vat >= 0 ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net VAT Position</p>
              <p className={`text-2xl font-bold ${data.net_vat >= 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                RWF {formatNumber(Math.abs(data.net_vat))}
              </p>
            </div>
          </div>
          <p className={`text-xs font-medium mt-1 ${data.net_vat >= 0 ? 'text-amber-600' : 'text-blue-600'}`}>
            {data.net_vat_position === 'payable' ? 'VAT Payable to RRA' : data.net_vat_position === 'refundable' ? 'VAT Refundable from RRA' : 'No VAT Due'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="flex border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-primary text-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`vat-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'summary' && (
            <div className="space-y-6" data-testid="vat-summary-tab">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Description</th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Total (incl. VAT)</th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Total (excl. VAT)</th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-foreground">VAT Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr className="hover:bg-muted/30">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">Sales (Output VAT)</td>
                      <td className="px-6 py-4 text-sm text-right">RWF {formatNumber(data.output_vat.total_incl_vat)}</td>
                      <td className="px-6 py-4 text-sm text-right">RWF {formatNumber(data.output_vat.total_excl_vat)}</td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">RWF {formatNumber(data.output_vat.total_vat)}</td>
                    </tr>
                    <tr className="hover:bg-muted/30">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">Purchases & Expenses (Input VAT)</td>
                      <td className="px-6 py-4 text-sm text-right">RWF {formatNumber(data.input_vat.total_incl_vat)}</td>
                      <td className="px-6 py-4 text-sm text-right">RWF {formatNumber(data.input_vat.total_excl_vat)}</td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">RWF {formatNumber(data.input_vat.total_vat)}</td>
                    </tr>
                    <tr className="bg-muted/30 font-semibold">
                      <td className="px-6 py-4 text-sm">Net VAT ({data.net_vat_position === 'payable' ? 'Payable' : data.net_vat_position === 'refundable' ? 'Refundable' : 'Zero'})</td>
                      <td className="px-6 py-4 text-sm text-right"></td>
                      <td className="px-6 py-4 text-sm text-right"></td>
                      <td className={`px-6 py-4 text-sm text-right font-bold ${data.net_vat >= 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                        RWF {formatNumber(Math.abs(data.net_vat))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {periodFilter.label && (
                <p className="text-sm text-muted-foreground">Period: {periodFilter.label}</p>
              )}
            </div>
          )}

          {activeTab === 'outputs' && (
            <div data-testid="vat-outputs-tab">
              <h3 className="text-lg font-semibold mb-4">VAT Output Details (Sales)</h3>
              {data.output_vat.details.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No VAT output transactions found for this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Customer</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Date</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Total (incl.)</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Excl. VAT</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">VAT (18%)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.output_vat.details.map((item) => (
                        <tr key={item.order_id || `${item.customer}-${item.date}`} className="hover:bg-muted/30">
                          <td className="px-6 py-3 text-sm">{item.customer}</td>
                          <td className="px-6 py-3 text-sm text-muted-foreground">{new Date(item.date).toLocaleDateString()}</td>
                          <td className="px-6 py-3 text-sm text-right">RWF {formatNumber(item.total_incl)}</td>
                          <td className="px-6 py-3 text-sm text-right">RWF {formatNumber(item.total_excl)}</td>
                          <td className="px-6 py-3 text-sm text-right font-medium text-red-600">RWF {formatNumber(item.vat_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/30 border-t-2 border-border">
                      <tr>
                        <td className="px-6 py-3 text-sm font-semibold" colSpan={2}>Total</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold">RWF {formatNumber(data.output_vat.total_incl_vat)}</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold">RWF {formatNumber(data.output_vat.total_excl_vat)}</td>
                        <td className="px-6 py-3 text-sm text-right font-bold text-red-600">RWF {formatNumber(data.output_vat.total_vat)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'inputs' && (
            <div data-testid="vat-inputs-tab">
              <h3 className="text-lg font-semibold mb-4">VAT Input Details (Purchases & Expenses)</h3>
              {data.input_vat.details.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No VAT input transactions found. Mark purchases/expenses as "VAT Inclusive" to track input VAT.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Type</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Description</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Date</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Total (incl.)</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">Excl. VAT</th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-foreground">VAT (18%)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.input_vat.details.map((item) => (
                        <tr key={item.id || `${item.type}-${item.description}-${item.date}`} className="hover:bg-muted/30">
                          <td className="px-6 py-3 text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              item.type === 'purchase' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>{item.type}</span>
                          </td>
                          <td className="px-6 py-3 text-sm">{item.description}</td>
                          <td className="px-6 py-3 text-sm text-muted-foreground">{new Date(item.date).toLocaleDateString()}</td>
                          <td className="px-6 py-3 text-sm text-right">RWF {formatNumber(item.total_incl)}</td>
                          <td className="px-6 py-3 text-sm text-right">RWF {formatNumber(item.total_excl)}</td>
                          <td className="px-6 py-3 text-sm text-right font-medium text-green-600">RWF {formatNumber(item.vat_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/30 border-t-2 border-border">
                      <tr>
                        <td className="px-6 py-3 text-sm font-semibold" colSpan={3}>Total</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold">RWF {formatNumber(data.input_vat.total_incl_vat)}</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold">RWF {formatNumber(data.input_vat.total_excl_vat)}</td>
                        <td className="px-6 py-3 text-sm text-right font-bold text-green-600">RWF {formatNumber(data.input_vat.total_vat)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VATTrackingPage;
